/**
 * Note: Pressure is in kPa. Temperature is in Celsius.
 * These examples are grouped by diagnostic condition to demonstrate how various
 * system problems appear across different refrigerants.
 */

import { PressureUnit, TemperatureUnit } from '../types';

export interface ExampleReading {
  temperature: number;
  temperatureUnit: TemperatureUnit;
  pressure: number;
  pressureUnit: PressureUnit;
  type: 'Suction Line' | 'Liquid Line';
}

export interface RefrigerantExampleSet {
  [refrigerantCode: string]: ExampleReading[];
}

export interface GroupedHvacConditions {
  [condition: string]: RefrigerantExampleSet;
}

export const hvacExamplesByCondition: GroupedHvacConditions = {
  Optimal: {
    R22: [
      {
        temperature: 15.6,
        temperatureUnit: TemperatureUnit.C,
        pressure: 584,
        pressureUnit: PressureUnit.KPAA,
        type: 'Suction Line'
      },
      {
        temperature: 42.1,
        temperatureUnit: TemperatureUnit.C,
        pressure: 1930,
        pressureUnit: PressureUnit.KPAA,
        type: 'Liquid Line'
      }
    ],
    R134a: [
      {
        temperature: 4,
        temperatureUnit: TemperatureUnit.C,
        pressure: 241,
        pressureUnit: PressureUnit.KPAA,
        type: 'Suction Line'
      },
      {
        temperature: 40,
        temperatureUnit: TemperatureUnit.C,
        pressure: 1172,
        pressureUnit: PressureUnit.KPAA,
        type: 'Liquid Line'
      }
    ],
    R410A: [
      {
        temperature: 11,
        temperatureUnit: TemperatureUnit.C,
        pressure: 934,
        pressureUnit: PressureUnit.KPAA,
        type: 'Suction Line'
      },
      {
        temperature: 35,
        temperatureUnit: TemperatureUnit.C,
        pressure: 2382,
        pressureUnit: PressureUnit.KPAA,
        type: 'Liquid Line'
      }
    ],
    R404A: [
      {
        temperature: 8.3,
        temperatureUnit: TemperatureUnit.C,
        pressure: 633,
        pressureUnit: PressureUnit.KPAA,
        type: 'Suction Line'
      },
      {
        temperature: 35.6,
        temperatureUnit: TemperatureUnit.C,
        pressure: 1913,
        pressureUnit: PressureUnit.KPAA,
        type: 'Liquid Line'
      }
    ],
    R1234yf: [
      {
        temperature: 12,
        temperatureUnit: TemperatureUnit.C,
        pressure: 392,
        pressureUnit: PressureUnit.KPAA,
        type: 'Suction Line'
      },
      {
        temperature: 35,
        temperatureUnit: TemperatureUnit.C,
        pressure: 1015,
        pressureUnit: PressureUnit.KPAA,
        type: 'Liquid Line'
      }
    ],
    R407C: [
      {
        temperature: 13.6,
        temperatureUnit: TemperatureUnit.C,
        pressure: 583,
        pressureUnit: PressureUnit.KPAA,
        type: 'Suction Line'
      },
      {
        temperature: 37.3,
        temperatureUnit: TemperatureUnit.C,
        pressure: 1664,
        pressureUnit: PressureUnit.KPAA,
        type: 'Liquid Line'
      }
    ],
    R32: [
      {
        temperature: 10.8,
        temperatureUnit: TemperatureUnit.C,
        pressure: 1044,
        pressureUnit: PressureUnit.KPAA,
        type: 'Suction Line'
      },
      {
        temperature: 38.8,
        temperatureUnit: TemperatureUnit.C,
        pressure: 2434,
        pressureUnit: PressureUnit.KPAA,
        type: 'Liquid Line'
      }
    ]
  },
  Undercharged: {
    R22: [
      {
        temperature: 16.7,
        temperatureUnit: TemperatureUnit.C,
        pressure: 482,
        pressureUnit: PressureUnit.KPAA,
        type: 'Suction Line'
      },
      {
        temperature: 42.2,
        temperatureUnit: TemperatureUnit.C,
        pressure: 1655,
        pressureUnit: PressureUnit.KPAA,
        type: 'Liquid Line'
      }
    ],
    R134a: [
      {
        temperature: 15,
        temperatureUnit: TemperatureUnit.C,
        pressure: 172,
        pressureUnit: PressureUnit.KPAA,
        type: 'Suction Line'
      },
      {
        temperature: 40,
        temperatureUnit: TemperatureUnit.C,
        pressure: 965,
        pressureUnit: PressureUnit.KPAA,
        type: 'Liquid Line'
      }
    ],
    R410A: [
      {
        temperature: 10,
        temperatureUnit: TemperatureUnit.C,
        pressure: 750,
        pressureUnit: PressureUnit.KPAA,
        type: 'Suction Line'
      },
      {
        temperature: 32,
        temperatureUnit: TemperatureUnit.C,
        pressure: 2000,
        pressureUnit: PressureUnit.KPAA,
        type: 'Liquid Line'
      }
    ],
    R404A: [
      {
        temperature: 10,
        temperatureUnit: TemperatureUnit.C,
        pressure: 503,
        pressureUnit: PressureUnit.KPAA,
        type: 'Suction Line'
      },
      {
        temperature: 38.9,
        temperatureUnit: TemperatureUnit.C,
        pressure: 1655,
        pressureUnit: PressureUnit.KPAA,
        type: 'Liquid Line'
      }
    ],
    R1234yf: [
      {
        temperature: 15,
        temperatureUnit: TemperatureUnit.C,
        pressure: 300,
        pressureUnit: PressureUnit.KPAA,
        type: 'Suction Line'
      },
      {
        temperature: 38,
        temperatureUnit: TemperatureUnit.C,
        pressure: 850,
        pressureUnit: PressureUnit.KPAA,
        type: 'Liquid Line'
      }
    ],
    R407C: [
      {
        temperature: 16.7,
        temperatureUnit: TemperatureUnit.C,
        pressure: 469,
        pressureUnit: PressureUnit.KPAA,
        type: 'Suction Line'
      },
      {
        temperature: 41.1,
        temperatureUnit: TemperatureUnit.C,
        pressure: 1448,
        pressureUnit: PressureUnit.KPAA,
        type: 'Liquid Line'
      }
    ],
    R32: [
      {
        temperature: 13.9,
        temperatureUnit: TemperatureUnit.C,
        pressure: 827,
        pressureUnit: PressureUnit.KPAA,
        type: 'Suction Line'
      },
      {
        temperature: 42.2,
        temperatureUnit: TemperatureUnit.C,
        pressure: 2068,
        pressureUnit: PressureUnit.KPAA,
        type: 'Liquid Line'
      }
    ]
  },
  Overcharged: {
    R22: [
      {
        temperature: 7.8,
        temperatureUnit: TemperatureUnit.C,
        pressure: 689,
        pressureUnit: PressureUnit.KPAA,
        type: 'Suction Line'
      },
      {
        temperature: 35,
        temperatureUnit: TemperatureUnit.C,
        pressure: 2275,
        pressureUnit: PressureUnit.KPAA,
        type: 'Liquid Line'
      }
    ],
    R134a: [
      {
        temperature: 6.8,
        temperatureUnit: TemperatureUnit.C,
        pressure: 310,
        pressureUnit: PressureUnit.KPAA,
        type: 'Suction Line'
      },
      {
        temperature: 32.7,
        temperatureUnit: TemperatureUnit.C,
        pressure: 1379,
        pressureUnit: PressureUnit.KPAA,
        type: 'Liquid Line'
      }
    ],
    R410A: [
      {
        temperature: 10,
        temperatureUnit: TemperatureUnit.C,
        pressure: 1050,
        pressureUnit: PressureUnit.KPAA,
        type: 'Suction Line'
      },
      {
        temperature: 36,
        temperatureUnit: TemperatureUnit.C,
        pressure: 2800,
        pressureUnit: PressureUnit.KPAA,
        type: 'Liquid Line'
      }
    ],
    R404A: [
      {
        temperature: 6.1,
        temperatureUnit: TemperatureUnit.C,
        pressure: 765,
        pressureUnit: PressureUnit.KPAA,
        type: 'Suction Line'
      },
      {
        temperature: 32.2,
        temperatureUnit: TemperatureUnit.C,
        pressure: 2206,
        pressureUnit: PressureUnit.KPAA,
        type: 'Liquid Line'
      }
    ],
    R1234yf: [
      {
        temperature: 7,
        temperatureUnit: TemperatureUnit.C,
        pressure: 450,
        pressureUnit: PressureUnit.KPAA,
        type: 'Suction Line'
      },
      {
        temperature: 30,
        temperatureUnit: TemperatureUnit.C,
        pressure: 1200,
        pressureUnit: PressureUnit.KPAA,
        type: 'Liquid Line'
      }
    ],
    R407C: [
      {
        temperature: 7.8,
        temperatureUnit: TemperatureUnit.C,
        pressure: 689,
        pressureUnit: PressureUnit.KPAA,
        type: 'Suction Line'
      },
      {
        temperature: 33.9,
        temperatureUnit: TemperatureUnit.C,
        pressure: 1930,
        pressureUnit: PressureUnit.KPAA,
        type: 'Liquid Line'
      }
    ],
    R32: [
      {
        temperature: 6.7,
        temperatureUnit: TemperatureUnit.C,
        pressure: 1241,
        pressureUnit: PressureUnit.KPAA,
        type: 'Suction Line'
      },
      {
        temperature: 35,
        temperatureUnit: TemperatureUnit.C,
        pressure: 2827,
        pressureUnit: PressureUnit.KPAA,
        type: 'Liquid Line'
      }
    ]
  },
  Restriction: {
    R22: [
      {
        temperature: 21.1,
        temperatureUnit: TemperatureUnit.C,
        pressure: 414,
        pressureUnit: PressureUnit.KPAA,
        type: 'Suction Line'
      },
      {
        temperature: 37.7,
        temperatureUnit: TemperatureUnit.C,
        pressure: 2137,
        pressureUnit: PressureUnit.KPAA,
        type: 'Liquid Line'
      }
    ],
    R134a: [
      {
        temperature: 18.8,
        temperatureUnit: TemperatureUnit.C,
        pressure: 138,
        pressureUnit: PressureUnit.KPAA,
        type: 'Suction Line'
      },
      {
        temperature: 35,
        temperatureUnit: TemperatureUnit.C,
        pressure: 1241,
        pressureUnit: PressureUnit.KPAA,
        type: 'Liquid Line'
      }
    ],
    R410A: [
      {
        temperature: 10,
        temperatureUnit: TemperatureUnit.C,
        pressure: 680,
        pressureUnit: PressureUnit.KPAA,
        type: 'Suction Line'
      },
      {
        temperature: 35,
        temperatureUnit: TemperatureUnit.C,
        pressure: 2600,
        pressureUnit: PressureUnit.KPAA,
        type: 'Liquid Line'
      }
    ],
    R404A: [
      {
        temperature: 12.2,
        temperatureUnit: TemperatureUnit.C,
        pressure: 448,
        pressureUnit: PressureUnit.KPAA,
        type: 'Suction Line'
      },
      {
        temperature: 34.4,
        temperatureUnit: TemperatureUnit.C,
        pressure: 2068,
        pressureUnit: PressureUnit.KPAA,
        type: 'Liquid Line'
      }
    ],
    R1234yf: [
      {
        temperature: 20,
        temperatureUnit: TemperatureUnit.C,
        pressure: 250,
        pressureUnit: PressureUnit.KPAA,
        type: 'Suction Line'
      },
      {
        temperature: 32,
        temperatureUnit: TemperatureUnit.C,
        pressure: 1100,
        pressureUnit: PressureUnit.KPAA,
        type: 'Liquid Line'
      }
    ],
    R407C: [
      {
        temperature: 19.4,
        temperatureUnit: TemperatureUnit.C,
        pressure: 414,
        pressureUnit: PressureUnit.KPAA,
        type: 'Suction Line'
      },
      {
        temperature: 36.1,
        temperatureUnit: TemperatureUnit.C,
        pressure: 1862,
        pressureUnit: PressureUnit.KPAA,
        type: 'Liquid Line'
      }
    ],
    R32: [
      {
        temperature: 16.7,
        temperatureUnit: TemperatureUnit.C,
        pressure: 758,
        pressureUnit: PressureUnit.KPAA,
        type: 'Suction Line'
      },
      {
        temperature: 37.2,
        temperatureUnit: TemperatureUnit.C,
        pressure: 2620,
        pressureUnit: PressureUnit.KPAA,
        type: 'Liquid Line'
      }
    ]
  },
  'Inefficient Compressor': {
    R22: [
      {
        temperature: 8.9,
        temperatureUnit: TemperatureUnit.C,
        pressure: 620,
        pressureUnit: PressureUnit.KPAA,
        type: 'Suction Line'
      },
      {
        temperature: 43.3,
        temperatureUnit: TemperatureUnit.C,
        pressure: 1517,
        pressureUnit: PressureUnit.KPAA,
        type: 'Liquid Line'
      }
    ],
    R134a: [
      {
        temperature: 8.8,
        temperatureUnit: TemperatureUnit.C,
        pressure: 276,
        pressureUnit: PressureUnit.KPAA,
        type: 'Suction Line'
      },
      {
        temperature: 41.3,
        temperatureUnit: TemperatureUnit.C,
        pressure: 827,
        pressureUnit: PressureUnit.KPAA,
        type: 'Liquid Line'
      }
    ],
    R410A: [
      {
        temperature: 8,
        temperatureUnit: TemperatureUnit.C,
        pressure: 827,
        pressureUnit: PressureUnit.KPAA,
        type: 'Suction Line'
      },
      {
        temperature: 38,
        temperatureUnit: TemperatureUnit.C,
        pressure: 1862,
        pressureUnit: PressureUnit.KPAA,
        type: 'Liquid Line'
      }
    ],
    R404A: [
      {
        temperature: 6.7,
        temperatureUnit: TemperatureUnit.C,
        pressure: 689,
        pressureUnit: PressureUnit.KPAA,
        type: 'Suction Line'
      },
      {
        temperature: 40,
        temperatureUnit: TemperatureUnit.C,
        pressure: 1517,
        pressureUnit: PressureUnit.KPAA,
        type: 'Liquid Line'
      }
    ],
    R1234yf: [
      {
        temperature: 6,
        temperatureUnit: TemperatureUnit.C,
        pressure: 400,
        pressureUnit: PressureUnit.KPAA,
        type: 'Suction Line'
      },
      {
        temperature: 40,
        temperatureUnit: TemperatureUnit.C,
        pressure: 800,
        pressureUnit: PressureUnit.KPAA,
        type: 'Liquid Line'
      }
    ],
    R407C: [
      {
        temperature: 8.3,
        temperatureUnit: TemperatureUnit.C,
        pressure: 620,
        pressureUnit: PressureUnit.KPAA,
        type: 'Suction Line'
      },
      {
        temperature: 42.2,
        temperatureUnit: TemperatureUnit.C,
        pressure: 1310,
        pressureUnit: PressureUnit.KPAA,
        type: 'Liquid Line'
      }
    ],
    R32: [
      {
        temperature: 6.1,
        temperatureUnit: TemperatureUnit.C,
        pressure: 965,
        pressureUnit: PressureUnit.KPAA,
        type: 'Suction Line'
      },
      {
        temperature: 43.3,
        temperatureUnit: TemperatureUnit.C,
        pressure: 1930,
        pressureUnit: PressureUnit.KPAA,
        type: 'Liquid Line'
      }
    ]
  }
};

// Legacy interface for backwards compatibility
export interface ExampleCondition {
  temperature: number;
  pressure: number;
  type: string;
}

export interface RefrigerantExamples {
  [refrigerantCode: string]: ExampleCondition[];
}

// Legacy data structure for backwards compatibility
export const exampleData: RefrigerantExamples = {
  R134a: [
    { temperature: 5, pressure: 350, type: '💧 Saturated' },
    { temperature: 40, pressure: 1200, type: '❄️ Subcooled' },
    { temperature: 2, pressure: 250, type: '🔥 Superheated' }
  ],
  R410A: [
    { temperature: 5, pressure: 934, type: '💧 Saturated' },
    { temperature: 35, pressure: 2382, type: '❄️ Subcooled' },
    { temperature: 14, pressure: 968, type: '🔥 Superheated' }
  ],
  R22: [
    { temperature: 5, pressure: 584, type: '💧 Saturated' },
    { temperature: 24, pressure: 1192, type: '❄️ Subcooled' },
    { temperature: 15, pressure: 584, type: '🔥 Superheated' }
  ],
  R404A: [
    { temperature: 5, pressure: 713, type: '💧 Saturated' },
    { temperature: 34, pressure: 1913, type: '❄️ Subcooled' },
    { temperature: 13, pressure: 633, type: '🔥 Superheated' }
  ],
  R32: [
    { temperature: 5, pressure: 1044, type: '💧 Saturated' },
    { temperature: 35, pressure: 2434, type: '❄️ Subcooled' },
    { temperature: 15, pressure: 1044, type: '🔥 Superheated' }
  ],
  R407C: [
    { temperature: 5, pressure: 583, type: '💧 Saturated' },
    { temperature: 35, pressure: 1664, type: '❄️ Subcooled' },
    { temperature: 15, pressure: 583, type: '🔥 Superheated' }
  ],
  R1234yf: [
    { temperature: 5, pressure: 372, type: '💧 Saturated' },
    { temperature: 35, pressure: 1015, type: '❄️ Subcooled' },
    { temperature: 12, pressure: 392, type: '🔥 Superheated' }
  ]
};
